import { useState, useMemo, useEffect, useCallback, useId } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { RefreshCw, Loader2, Plus } from 'lucide-react';
import JobsTable from '@/components/jobs/JobsTable';
import JobDetailsModal from '@/components/jobs/JobDetailsModal';
import SubmitJobModal from '@/components/jobs/SubmitJobModal';
import StatusFilter from '@/components/jobs/StatusFilter';
import DateRangeFilter from '@/components/jobs/DateRangeFilter';
import SearchInput from '@/components/common/SearchInput';
import { useInfiniteJobs, useInfiniteWorkerJobs, useCancelJob, useRetryJob } from '@/hooks/useJobs';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { getTimeRangeTimestamp } from '@/lib/utils';
import type { JobStatus, Job, TimeRange } from '@/lib/types';

// UUID validation regex (standard UUID format)
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Valid filter values for URL param validation
const VALID_STATUSES: (JobStatus | 'all')[] = ['all', 'pending', 'running', 'completed', 'failed', 'cancelled', 'expired', 'retry_pending'];
const VALID_DATE_RANGES: TimeRange[] = ['15m', '1h', '24h', '7d', '30d', 'all'];

export default function JobsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params with validation
  const urlStatus = searchParams.get('status');
  const initialStatus: JobStatus | 'all' =
    urlStatus && VALID_STATUSES.includes(urlStatus as JobStatus | 'all')
      ? (urlStatus as JobStatus | 'all')
      : 'all';

  const urlDateRange = searchParams.get('date_range');
  const initialDateRange: TimeRange =
    urlDateRange && VALID_DATE_RANGES.includes(urlDateRange as TimeRange)
      ? (urlDateRange as TimeRange)
      : 'all';

  const initialJobId = searchParams.get('job_id') || '';
  const initialWorkerId = searchParams.get('worker_id') || '';

  const [statusFilter, setStatusFilter] = useState<JobStatus | 'all'>(initialStatus);
  const [dateRange, setDateRange] = useState<TimeRange>(initialDateRange);
  const [jobIdSearch, setJobIdSearch] = useState(initialJobId);
  const [workerIdSearch, setWorkerIdSearch] = useState(initialWorkerId);
  const jobIdInputId = useId();
  const workerIdInputId = useId();
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [cancellingJobId, setCancellingJobId] = useState<string | null>(null);
  const [retryingJobId, setRetryingJobId] = useState<string | null>(null);
  const [modalJobIds, setModalJobIds] = useState<string[]>([]);
  const itemsPerPage = 50;

  // Cancel job mutation
  const cancelJobMutation = useCancelJob();

  // Retry job mutation
  const retryJobMutation = useRetryJob();

  // Convert date range to timestamp filter
  const timeRangeFilter = useMemo(() => getTimeRangeTimestamp(dateRange), [dateRange]);

  // UUID validator for Job ID search (empty values are allowed)
  const validateJobId = useCallback((value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (!UUID_REGEX.test(trimmed)) {
      return 'Invalid UUID format';
    }
    return null;
  }, []);

  // Check if job ID search has a valid UUID (for enabling/disabling query)
  const isJobIdValid = !jobIdSearch.trim() || UUID_REGEX.test(jobIdSearch.trim());

  // Sync all filters to URL
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      if (statusFilter !== 'all') {
        params.set('status', statusFilter);
      } else {
        params.delete('status');
      }
      if (dateRange !== 'all') {
        params.set('date_range', dateRange);
      } else {
        params.delete('date_range');
      }
      if (jobIdSearch.trim()) {
        params.set('job_id', jobIdSearch.trim());
      } else {
        params.delete('job_id');
      }
      if (workerIdSearch.trim()) {
        params.set('worker_id', workerIdSearch.trim());
      } else {
        params.delete('worker_id');
      }

      return params;
    }, { replace: true });
  }, [statusFilter, dateRange, jobIdSearch, workerIdSearch, setSearchParams]);

  // Build query params with useMemo for stable reference
  const queryParams = useMemo(() => {
    // Build custom query conditions for job ID search (worker ID is handled by dedicated endpoint)
    const queryConditions = [];

    // Only include job ID in query if it's a valid UUID
    if (jobIdSearch.trim() && isJobIdValid) {
      queryConditions.push(`id.eq:'${jobIdSearch.trim()}'`);
    }

    return {
      limit: itemsPerPage,
      sort_by: 'create_ts:desc',
      embed: 'assignments',
      fields: 'id,type_name,status_name,create_ts,start_ts,complete_ts,assignments(worker_id,release_ts)',
      status_name: statusFilter !== 'all' ? statusFilter : undefined,
      q: queryConditions.length > 0 ? queryConditions.join(' AND ') : undefined,
    };
  }, [statusFilter, jobIdSearch, isJobIdValid]);

  // Use worker-specific endpoint when filtering by worker ID
  const isWorkerFilter = !!workerIdSearch.trim();

  // Regular jobs query (when not filtering by worker)
  const jobsQuery = useInfiniteJobs(queryParams, timeRangeFilter, !isWorkerFilter);

  // Worker jobs query (when filtering by worker)
  const workerJobsQuery = useInfiniteWorkerJobs(
    workerIdSearch.trim(),
    queryParams,
    timeRangeFilter,
    isWorkerFilter
  );

  // Select active query based on filter mode
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    error,
  } = isWorkerFilter ? workerJobsQuery : jobsQuery;

  // Debug: Log error if present
  if (error) {
    console.error('useInfiniteJobs error:', error);
  }

  // Flatten all pages into a single jobs array
  const allJobs = useMemo(() => {
    return data?.pages.flatMap((page) => page.items) || [];
  }, [data]);

  // Infinite scroll with Intersection Observer
  const loadMoreRef = useInfiniteScroll(hasNextPage, isFetchingNextPage, fetchNextPage);

  const handleJobClick = (job: Job) => {
    setModalJobIds(allJobs.map(j => j.id)); // Snapshot current list for modal navigation
    setSelectedJobId(job.id);
    setIsDetailsModalOpen(true);
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleSubmitClick = () => {
    setIsSubmitModalOpen(true);
  };

  const handleCancelJob = async (jobId: string) => {
    setCancellingJobId(jobId);
    try {
      await cancelJobMutation.mutateAsync(jobId);
    } catch (error) {
      console.error('Failed to cancel job:', error);
      toast.error('Failed to cancel job. Please try again.');
    } finally {
      setCancellingJobId(null);
    }
  };

  const handleRetryJob = async (jobId: string) => {
    setRetryingJobId(jobId);
    try {
      await retryJobMutation.mutateAsync(jobId);
      toast.success('Job queued for retry');
    } catch (error) {
      console.error('Failed to retry job:', error);
      toast.error('Failed to retry job. Please try again.');
    } finally {
      setRetryingJobId(null);
    }
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Jobs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Complete job list with filtering and search
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSubmitClick}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground border border-primary transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span className="text-sm font-medium">Submit Job</span>
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span className="text-sm font-medium">Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Status Filter */}
          <StatusFilter value={statusFilter} onChange={setStatusFilter} />

          {/* Date Range Filter */}
          <DateRangeFilter value={dateRange} onChange={setDateRange} />

          {/* Job ID Search */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor={jobIdInputId} className="text-sm font-medium text-muted-foreground">Job ID</label>
            <SearchInput
              id={jobIdInputId}
              placeholder="Search by job ID..."
              value={jobIdSearch}
              onChange={setJobIdSearch}
              clearLabel="Clear Job ID search"
              validate={validateJobId}
            />
          </div>

          {/* Worker ID Search */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor={workerIdInputId} className="text-sm font-medium text-muted-foreground">Worker ID</label>
            <SearchInput
              id={workerIdInputId}
              placeholder="Search by worker ID..."
              value={workerIdSearch}
              onChange={setWorkerIdSearch}
              clearLabel="Clear Worker ID search"
            />
          </div>
        </div>

        {/* Active Filters Summary */}
        {(statusFilter !== 'all' || dateRange !== 'all' || jobIdSearch || workerIdSearch) && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {statusFilter !== 'all' && (
                <span className="px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium border border-primary/30">
                  Status: {statusFilter}
                </span>
              )}
              {dateRange !== 'all' && (
                <span className="px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium border border-primary/30">
                  Date: {dateRange}
                </span>
              )}
              {jobIdSearch && (
                <span className="px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium border border-primary/30">
                  Job ID: {jobIdSearch}
                </span>
              )}
              {workerIdSearch && (
                <span className="px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium border border-primary/30">
                  Worker ID: {workerIdSearch}
                </span>
              )}
              <button
                type="button"
                onClick={() => {
                  setStatusFilter('all');
                  setDateRange('all');
                  setJobIdSearch('');
                  setWorkerIdSearch('');
                }}
                className="text-xs text-muted-foreground hover:text-foreground underline"
              >
                Clear all
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="glass-card rounded-lg overflow-hidden">
        <JobsTable
          jobs={allJobs}
          loading={isLoading}
          onJobClick={handleJobClick}
          onCancelJob={handleCancelJob}
          onRetryJob={handleRetryJob}
          cancellingJobId={cancellingJobId}
          retryingJobId={retryingJobId}
        />
      </div>

      {/* Load more trigger */}
      {hasNextPage && (
        <div ref={loadMoreRef} className="flex items-center justify-center py-8">
          {isFetchingNextPage && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading more jobs...</span>
            </div>
          )}
        </div>
      )}

      {/* Results info */}
      {allJobs.length > 0 && (
        <div className="flex items-center justify-between px-4 py-3 glass-card rounded-lg">
          <p className="text-sm text-muted-foreground">
            Showing {allJobs.length} jobs {hasNextPage && '(scroll for more)'}
          </p>
          {!hasNextPage && (
            <p className="text-sm text-muted-foreground">
              All results loaded
            </p>
          )}
        </div>
      )}

      {/* Job Details Modal */}
      {selectedJobId && (
        <JobDetailsModal
          key={selectedJobId}
          jobId={selectedJobId}
          isOpen={isDetailsModalOpen}
          onClose={() => {
            setIsDetailsModalOpen(false);
            setSelectedJobId(null);
            setModalJobIds([]);
          }}
          jobIds={modalJobIds}
          onNavigate={(id) => setSelectedJobId(id)}
        />
      )}

      {/* Submit Job Modal */}
      <SubmitJobModal
        isOpen={isSubmitModalOpen}
        onClose={() => setIsSubmitModalOpen(false)}
      />
    </div>
  );
}
